import React, { useState, useEffect, useCallback } from 'react';
import DynamicSidebar from '../../components/ui/DynamicSidebar';
import DashboardHeader from '../../components/ui/DashboardHeader';
import Icon from '../../components/AppIcon';
import CreatePaymentLinkModal from './components/CreatePaymentLinkModal';
import EditPaymentLinkModal from './components/EditPaymentLinkModal';
import PaymentLinkDetailView from './components/PaymentLinkDetailView';
import { apiService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const METHOD_ICONS = {
  CARD: 'CreditCard',
  MOBILE_MONEY: 'Smartphone',
  CRYPTO: 'Bitcoin',
  // legacy lowercase fallback
  card: 'CreditCard',
  mobile_money: 'Smartphone',
  crypto: 'Bitcoin',
};

const isExpired = (expiryDate) => new Date() > new Date(expiryDate);

const formatExpiry = (date) => {
  try {
    return new Date(date)?.toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '-'; }
};

const formatCurrency = (amount, currency = 'KES') => {
  const num = parseFloat(amount);
  if (isNaN(num)) return '-';
  try {
    return new Intl.NumberFormat('en-KE', { style: 'currency', currency })?.format(num);
  } catch {
    return `${currency} ${num?.toFixed(2)}`;
  }
};

const StatusBadge = ({ status }) => {
  const apiStatus = status?.toUpperCase();

  if (apiStatus === 'PAID') {
    return (
      <span className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-semibold border bg-blue-500/10 text-blue-600 border-blue-500/20">
        <Icon name="BadgeCheck" size={11} />
        <span>Paid</span>
      </span>
    );
  }
  if (apiStatus === 'ACTIVE') {
    return (
      <span className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-semibold border bg-success/10 text-success border-success/20">
        <Icon name="CheckCircle" size={11} />
        <span>Active</span>
      </span>
    );
  }
  if (apiStatus === 'EXPIRED') {
    return (
      <span className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-semibold border bg-error/10 text-error border-error/20">
        <Icon name="XCircle" size={11} />
        <span>Expired</span>
      </span>
    );
  }
  if (apiStatus === 'CANCELLED') {
    return (
      <span className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-semibold border bg-warning/10 text-warning border-warning/20">
        <Icon name="Ban" size={11} />
        <span>Cancelled</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-semibold border bg-error/10 text-error border-error/20">
      <Icon name="XCircle" size={11} />
      <span>{status || 'Inactive'}</span>
    </span>
  );
};

const PAGE_SIZE = 20;

const PaymentLinkManagementPage = () => {
  const { businessCode } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [viewingLink, setViewingLink] = useState(null);
  const [links, setLinks] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [linkActionLoading, setLinkActionLoading] = useState(null);

  const fetchLinks = useCallback(async (pageNum = 0, statusFilter = '') => {
    if (!businessCode) return;
    setLoading(true);
    setFetchError('');
    try {
      const params = {
        page: pageNum,
        size: PAGE_SIZE,
        business_code: businessCode,
      };
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter?.toUpperCase();
      }
      const response = await apiService?.getPaymentLinks(params);
      const data = response?.data;
      const details = data?.details || [];
      if (pageNum === 0) {
        setLinks(details);
      } else {
        setLinks(prev => [...prev, ...details]);
      }
      setHasMore(details?.length === PAGE_SIZE);
      setPage(pageNum);
    } catch (err) {
      const errMsg = err?.response?.data?.status_description
        || err?.response?.data?.message
        || 'Failed to load payment links.';
      setFetchError(errMsg);
    } finally {
      setLoading(false);
    }
  }, [businessCode]);

  useEffect(() => {
    fetchLinks(0, filterStatus);
  }, [fetchLinks, filterStatus]);

  const handleCreated = ({ detail }) => {
    if (detail && Object.keys(detail)?.length > 0) {
      setLinks(prev => [detail, ...prev]);
    } else {
      fetchLinks(0, filterStatus);
    }
  };

  const handleSaved = (updatedLink) => {
    setLinks(prev => prev?.map(l => (l?.id === updatedLink?.id ? { ...l, ...updatedLink } : l)));
    setEditingLink(null);
  };

  const handleCopy = (id, url) => {
    navigator.clipboard?.writeText(url)?.then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const handleLoadMore = () => {
    fetchLinks(page + 1, filterStatus);
  };

  const filtered = links?.filter(l => {
    if (!search) return true;
    return l?.title?.toLowerCase()?.includes(search?.toLowerCase())
      || l?.id?.toLowerCase()?.includes(search?.toLowerCase())
      || l?.link_code?.toLowerCase()?.includes(search?.toLowerCase());
  });

  const activeCount = links?.filter(l => l?.status?.toUpperCase() === 'ACTIVE')?.length;
  const expiredCount = links?.filter(l => l?.status?.toUpperCase() === 'EXPIRED')?.length;

  const fetchLinkById = async (id, action) => {
    if (!businessCode) return;
    setLinkActionLoading(id);
    try {
      const response = await apiService?.getPaymentLinks({
        page: 0,
        size: 1,
        business_code: businessCode,
        id,
      });
      const details = response?.data?.details || [];
      const freshLink = details?.[0];
      if (freshLink) {
        if (action === 'view') setViewingLink(freshLink);
        else if (action === 'edit') setEditingLink(freshLink);
      }
    } catch (err) {
      console.error('Failed to fetch link by id', err);
      // fallback: use cached data
      const cached = links?.find(l => l?.id === id);
      if (cached) {
        if (action === 'view') setViewingLink(cached);
        else if (action === 'edit') setEditingLink(cached);
      }
    } finally {
      setLinkActionLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <DashboardHeader onMenuToggle={() => setSidebarCollapsed(prev => !prev)} />
      <div className="flex">
        <DynamicSidebar
          isCollapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(prev => !prev)}
        />
        <main className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'} mt-16 p-6`}>

          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-center space-x-2 text-sm text-text-secondary mb-1">
              <span>Payment Operations</span>
              <Icon name="ChevronRight" size={14} />
              <span className="text-text-primary font-medium">Payment Link Management</span>
            </div>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h1 className="text-2xl font-bold text-text-primary">Payment Link Management</h1>
                <p className="text-text-secondary text-sm mt-1">Generate and share secure payment links with configurable methods and expiration</p>
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                <Icon name="Plus" size={16} />
                <span>Create Payment Link</span>
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-surface border border-border rounded-xl p-4 flex items-center space-x-4">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="Link" size={20} className="text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-text-primary">{links?.length}</p>
                <p className="text-xs text-text-secondary">Total Links</p>
              </div>
            </div>
            <div className="bg-surface border border-border rounded-xl p-4 flex items-center space-x-4">
              <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="CheckCircle" size={20} className="text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-text-primary">{activeCount}</p>
                <p className="text-xs text-text-secondary">Active Links</p>
              </div>
            </div>
            <div className="bg-surface border border-border rounded-xl p-4 flex items-center space-x-4">
              <div className="w-10 h-10 bg-error/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="XCircle" size={20} className="text-error" />
              </div>
              <div>
                <p className="text-2xl font-bold text-text-primary">{expiredCount}</p>
                <p className="text-xs text-text-secondary">Expired Links</p>
              </div>
            </div>
          </div>

          {/* Filters & View Toggle */}
          <div className="bg-surface border border-border rounded-xl p-4 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 relative">
                <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e?.target?.value)}
                  placeholder="Search by title or link ID..."
                  className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg bg-background text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e?.target?.value)}
                className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <option value="">All Status</option>
                <option value="ACTIVE">Active</option>
                <option value="EXPIRED">Expired</option>
                <option value="PAID">Paid</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
              <button
                onClick={() => fetchLinks(1, filterStatus)}
                disabled={loading}
                className="flex items-center space-x-1.5 px-3 py-2 text-sm border border-border rounded-lg bg-background text-text-secondary hover:bg-muted transition-colors disabled:opacity-50"
                title="Refresh"
              >
                <Icon name={loading ? 'Loader' : 'RefreshCw'} size={15} className={loading ? 'animate-spin' : ''} />
                <span className="hidden sm:inline">Refresh</span>
              </button>
              <div className="flex items-center border border-border rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-primary text-white' : 'bg-background text-text-secondary hover:bg-muted'}`}
                >
                  <Icon name="LayoutGrid" size={16} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-primary text-white' : 'bg-background text-text-secondary hover:bg-muted'}`}
                >
                  <Icon name="List" size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* Fetch Error */}
          {fetchError && (
            <div className="flex items-center space-x-2 p-4 bg-error/10 border border-error/20 rounded-xl mb-6">
              <Icon name="AlertCircle" size={16} className="text-error flex-shrink-0" />
              <p className="text-sm text-error">{fetchError}</p>
              <button onClick={() => fetchLinks(1, filterStatus)} className="ml-auto text-xs text-error underline">Retry</button>
            </div>
          )}

          {/* Loading skeleton */}
          {loading && links?.length === 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {[1, 2, 3]?.map(i => (
                <div key={i} className="bg-surface border border-border rounded-xl p-5 animate-pulse">
                  <div className="h-4 bg-muted rounded w-3/4 mb-3" />
                  <div className="h-3 bg-muted rounded w-1/2 mb-4" />
                  <div className="h-6 bg-muted rounded w-1/3 mb-3" />
                  <div className="h-3 bg-muted rounded w-full" />
                </div>
              ))}
            </div>
          )}

          {/* Links — Grid View */}
          {!loading && filtered?.length === 0 && !fetchError ? (
            <div className="bg-surface border border-border rounded-xl p-12 text-center">
              <div className="w-14 h-14 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Link" size={24} className="text-text-secondary" />
              </div>
              <h3 className="text-base font-semibold text-text-primary mb-1">No payment links found</h3>
              <p className="text-sm text-text-secondary mb-4">Create your first payment link to get started.</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                <Icon name="Plus" size={15} />
                <span>Create Payment Link</span>
              </button>
            </div>
          ) : filtered?.length > 0 && viewMode === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {filtered?.map(item => {
                const expired = isExpired(item?.expires_at);
                const methods = item?.payment_methods || [];
                const isCopied = copiedId === item?.id;
                const canEdit = item?.status?.toUpperCase() === 'ACTIVE';
                const isActionLoading = linkActionLoading === item?.id;
                return (
                  <div key={item?.id} className={`bg-surface border rounded-xl p-5 flex flex-col space-y-4 transition-shadow hover:shadow-md ${item?.status?.toUpperCase() === 'EXPIRED' ? 'border-error/20 opacity-80' : 'border-border'}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-text-primary truncate">{item?.title}</p>
                        <p className="text-xs text-text-secondary font-mono mt-0.5 truncate">{item?.id}</p>
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                        <StatusBadge status={item?.status} />
                        <button
                          onClick={() => fetchLinkById(item?.id, 'view')}
                          disabled={isActionLoading}
                          title="View details"
                          className="p-1.5 rounded-lg transition-colors text-text-secondary hover:bg-muted hover:text-primary disabled:opacity-50"
                        >
                          {isActionLoading ? <Icon name="Loader" size={14} className="animate-spin" /> : <Icon name="Eye" size={14} />}
                        </button>
                        <button
                          onClick={() => canEdit && fetchLinkById(item?.id, 'edit')}
                          disabled={!canEdit || isActionLoading}
                          title={canEdit ? 'Edit payment link' : 'Cannot edit expired or paid links'}
                          className={`p-1.5 rounded-lg transition-colors ${canEdit && !isActionLoading ? 'text-text-secondary hover:bg-muted hover:text-primary' : 'text-text-secondary/30 cursor-not-allowed'}`}
                        >
                          <Icon name="Pencil" size={14} />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xl font-bold text-text-primary">{formatCurrency(item?.amount, item?.currency)}</p>
                        {item?.description && <p className="text-xs text-text-secondary mt-0.5 truncate">{item?.description}</p>}
                      </div>
                      {item?.business_name && (
                        <p className="text-xs text-text-secondary truncate max-w-[120px]">{item?.business_name}</p>
                      )}
                    </div>
                    {/* Methods */}
                    <div className="flex items-center space-x-2">
                      <p className="text-xs text-text-secondary">Methods:</p>
                      <div className="flex items-center space-x-1.5">
                        {methods?.map(m => (
                          <div key={m} className="w-6 h-6 bg-primary/10 rounded flex items-center justify-center" title={m?.replace('_', ' ')}>
                            <Icon name={METHOD_ICONS?.[m] || 'CreditCard'} size={12} className="text-primary" />
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Expiry */}
                    <div className="flex items-center space-x-1.5 text-xs text-text-secondary">
                      <Icon name="Clock" size={12} />
                      <span>{item?.status?.toUpperCase() === 'EXPIRED' ? 'Expired' : 'Expires'}: {formatExpiry(item?.expires_at)}</span>
                    </div>
                    {/* Link + Actions */}
                    <div className="pt-2 border-t border-border space-y-2">
                      <div className="flex items-center space-x-2">
                        <input
                          readOnly
                          value={item?.link_code || ''}
                          className="flex-1 px-2 py-1.5 text-xs border border-border rounded-lg bg-muted text-text-secondary truncate focus:outline-none"
                        />
                        <button
                          onClick={() => canEdit && handleCopy(item?.id, item?.link_code)}
                          disabled={!canEdit}
                          className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex-shrink-0 ${isCopied ? 'bg-success text-white' : !canEdit ? 'bg-muted text-text-secondary/40 cursor-not-allowed' : 'bg-primary text-white hover:bg-primary/90'}`}
                        >
                          <Icon name={isCopied ? 'Check' : 'Copy'} size={12} />
                          <span>{isCopied ? 'Copied' : 'Copy'}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : filtered?.length > 0 ? (
            /* List View */
            <div className="bg-surface border border-border rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide">Title / ID</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide">Methods</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide">Expires</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filtered?.map(item => {
                      const expired = isExpired(item?.expires_at);
                      const methods = item?.payment_methods || [];
                      const isCopied = copiedId === item?.id;
                      const canEdit = item?.status?.toUpperCase() === 'ACTIVE';
                      const isActionLoading = linkActionLoading === item?.id;
                      return (
                        <tr key={item?.id} className={`hover:bg-muted/20 transition-colors ${item?.status?.toUpperCase() === 'EXPIRED' ? 'opacity-75' : ''}`}>
                          <td className="px-4 py-3">
                            <p className="font-medium text-text-primary">{item?.title}</p>
                            <p className="text-xs text-text-secondary font-mono">{item?.id}</p>
                          </td>
                          <td className="px-4 py-3 font-semibold text-text-primary whitespace-nowrap">
                            {formatCurrency(item?.amount, item?.currency)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-1">
                              {methods?.map(m => (
                                <div key={m} className="w-6 h-6 bg-primary/10 rounded flex items-center justify-center" title={m?.replace('_', ' ')}>
                                  <Icon name={METHOD_ICONS?.[m] || 'CreditCard'} size={12} className="text-primary" />
                                </div>
                              ))}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-text-secondary whitespace-nowrap">
                            {formatExpiry(item?.expires_at)}
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge status={item?.status} />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => fetchLinkById(item?.id, 'view')}
                                disabled={isActionLoading}
                                className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors bg-muted text-text-secondary hover:bg-primary/10 hover:text-primary disabled:opacity-50"
                              >
                                {isActionLoading ? <Icon name="Loader" size={12} className="animate-spin" /> : <Icon name="Eye" size={12} />}
                                <span>View</span>
                              </button>
                              <button
                                onClick={() => canEdit && handleCopy(item?.id, item?.link_code)}
                                disabled={!canEdit}
                                className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex-shrink-0 ${isCopied ? 'bg-success text-white' : !canEdit ? 'bg-muted text-text-secondary/40 cursor-not-allowed' : 'bg-primary text-white hover:bg-primary/90'}`}
                              >
                                <Icon name={isCopied ? 'Check' : 'Copy'} size={12} />
                                <span>{isCopied ? 'Copied' : 'Copy'}</span>
                              </button>
                              <button
                                onClick={() => canEdit && fetchLinkById(item?.id, 'edit')}
                                disabled={!canEdit || isActionLoading}
                                title={canEdit ? 'Edit payment link' : 'Cannot edit expired or paid links'}
                                className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${canEdit && !isActionLoading ? 'bg-muted text-text-secondary hover:bg-warning/10 hover:text-warning' : 'bg-muted text-text-secondary/30 cursor-not-allowed'}`}
                              >
                                <Icon name="Pencil" size={12} />
                                <span>Edit</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {/* Load More */}
          {hasMore && !loading && (
            <div className="flex justify-center mt-6">
              <button
                onClick={handleLoadMore}
                className="flex items-center space-x-2 px-5 py-2 border border-border rounded-lg text-sm font-medium text-text-secondary hover:bg-muted transition-colors"
              >
                <Icon name="ChevronDown" size={15} />
                <span>Load More</span>
              </button>
            </div>
          )}
          {loading && links?.length > 0 && (
            <div className="flex justify-center mt-6">
              <Icon name="Loader" size={20} className="animate-spin text-primary" />
            </div>
          )}
        </main>
      </div>
      {showCreateModal && (
        <CreatePaymentLinkModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleCreated}
        />
      )}
      {editingLink && (
        <EditPaymentLinkModal
          link={editingLink}
          onClose={() => setEditingLink(null)}
          onSaved={handleSaved}
        />
      )}
      {viewingLink && (
        <PaymentLinkDetailView
          link={viewingLink}
          onClose={() => setViewingLink(null)}
        />
      )}
    </div>
  );
};

export default PaymentLinkManagementPage;
